'use client';
import { debounce, isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { useTranslations } from 'next-intl';
import { CatHeading } from 'components/blocks-components';
import {
  Conditional,
  CustomStyledLabel,
  MyEquipmentSearch
} from 'components/pcc-components';
import { ViewAll } from '../view-all';
import { EquipmentList } from './equipment-list/equipment-list';
import { EquipmentListProps } from '../../types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getFilteredList } from 'services/equipment/utils';
import { MY_EQUIPMENT_SEARCH_KEYS } from 'services/equipment/constants';
import { AddEquipmentButton } from 'components/my-equipment/add-equipment-button';
import { NoEquipmentSearchResult } from '../no-equipment-search-container/no-equipment-serch-result';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import {
  DEBOUNCE_TIME_FIREVENT,
  STATUS_FAIL,
  STATUS_SUCCESS
} from 'constants/analyticsConstants';

export const EquipmentListWrapper = ({
  equipmentList,
  selectedEquipment,
  userDetail,
  serviceInsights,
  isVirtualScrollFeatureEnabled,
  startTransition
}: Readonly<EquipmentListProps>) => {
  const t = useTranslations();
  const hasSelectedEquipment = !isEmpty(selectedEquipment);
  const equipmentListTitle = hasSelectedEquipment
    ? t('CHANGE_EQUIPMENT')
    : t('SELECT_EQUIPMENT');
  const btnText = t('MEQ_ADD_EQUIP');
  const [searchInput, setSearchInput] = useState('');
  const [equipments, setEquipments] = useState(equipmentList);
  const { fireEvent } = useFormSubmitEvent();
  const debouncedFireEvent = useMemo(
    () =>
      debounce((searchTerm: string, isSuccess: boolean) => {
        if (searchTerm) {
          fireEvent({
            formName: 'search my equipment',
            formContent: searchTerm,
            formStatus: isSuccess ? STATUS_SUCCESS : STATUS_FAIL,
            formFieldCausingError: isSuccess ? '' : 'no search results',
            formLocation: 'PCC Home Page'
          });
        }
      }, DEBOUNCE_TIME_FIREVENT),
    [fireEvent]
  );

  const searchEquipment = useCallback(
    (searchTerm) => {
      setSearchInput(searchTerm);
      const filteredList = getFilteredList(
        equipmentList,
        searchTerm,
        MY_EQUIPMENT_SEARCH_KEYS
      );
      const isSuccess = !isEmpty(filteredList);
      debouncedFireEvent(searchTerm, isSuccess);
      setEquipments(filteredList);
    },
    [equipmentList, debouncedFireEvent]
  );

  useEffect(() => {
    setEquipments(equipmentList);
  }, [equipmentList]);
  const viewAllLabel = `${t('VIEW_ALL')} (${equipmentList.length})`;
  return (
    <>
      <div className="mb-0 mb-md-2 pb-md-1 d-flex justify-content-between align-items-center">
        <CatHeading variant="label">
          <CustomStyledLabel>{equipmentListTitle}</CustomStyledLabel>
        </CatHeading>
        <ViewAll>{viewAllLabel}</ViewAll>
      </div>
      <div className="mb-md-2 mt-md-1 row g-0">
        <div className="col-7" data-testid="search-bar">
          <MyEquipmentSearch
            placeholder={t('MEQ_SEARCH_TEXT')}
            onSearch={(searchTerm) => searchEquipment(searchTerm)}
            searchFormProps={{ value: searchInput }}
          />
        </div>
        <div className="col-5 ps-3">
          <AddEquipmentButton size="md" aria-label={btnText}>
            {btnText}
          </AddEquipmentButton>
        </div>
      </div>
      <Conditional
        test={!isEmpty(equipments)}
        fallback={<NoEquipmentSearchResult />}
      >
        <EquipmentList
          equipmentList={equipments}
          selectedEquipment={selectedEquipment}
          userDetail={userDetail}
          searchTerm={searchInput}
          serviceInsights={serviceInsights}
          setSearchInput={setSearchInput}
          isVirtualScrollFeatureEnabled={isVirtualScrollFeatureEnabled}
          startTransition={startTransition}
        />
      </Conditional>
    </>
  );
};
